import { Action, DEFAULT_LIMIT, DEFAULT_PAGE } from "@pilatestime/shared-types";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Plus, Search, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import type { PaginationMeta } from "@/components/data-table";
import {
	ListPageCard,
	ListPageCardContent,
	ListPageCardHeader,
} from "@/components/ListPageCard";
import { PageHeader } from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Pagination } from "@/components/ui/pagination";
import { useCan } from "@/hooks/useAccessControl";
import { useApiParameters } from "@/hooks/useApiParameters";
import { InvoiceList } from "./-components/InvoiceList";

// Search params schema
const billingSearchSchema = z.object({
	page: z.number().min(1).catch(DEFAULT_PAGE),
	limit: z.number().min(1).max(100).catch(DEFAULT_LIMIT),
	search: z.string().catch(""),
});

export const Route = createFileRoute("/_auth/app/_app/org/$orgId/billing/")({
	beforeLoad: ({ context }) => {
		if (!context.userId) {
			throw new Error("Not authenticated");
		}
	},
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		return { userId, token, orgId: params.orgId };
	},
	validateSearch: billingSearchSchema,
	component: BillingIndexPage,
});

function BillingIndexPage() {
	const { t } = useTranslation();
	const { userId, token, orgId } = Route.useLoaderData();
	const search = Route.useSearch();
	const navigate = useNavigate();

	// Access control
	const can = useCan();
	const canManageBilling = can(Action.MANAGE_PAYMENTS);

	// Local state for pagination and search
	const [currentPage, setCurrentPage] = useState(search.page);
	const [pageSize, setPageSize] = useState(search.limit);
	const [paginationMeta, setPaginationMeta] = useState<PaginationMeta>({
		page: currentPage,
		limit: pageSize,
		total: 0,
		totalPages: 0,
		hasNextPage: false,
		hasPreviousPage: false,
	});
	const [searchTerm, setSearchTerm] = useState(search.search);
	const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(search.search);

	// Navigate to invoice details page
	const handleViewInvoice = useCallback(
		(invoiceId: string) => {
			navigate({
				to: "/app/org/$orgId/billing/$invoiceId/overview",
				params: { orgId, invoiceId },
			});
		},
		[navigate, orgId],
	);

	// Debounce search term
	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearchTerm(searchTerm);
			setCurrentPage(1);
		}, 300);
		return () => clearTimeout(timer);
	}, [searchTerm]);

	// Pagination handlers
	const handlePageChange = (page: number) => {
		setCurrentPage(page);
	};

	const handlePageSizeChange = (limit: number) => {
		setPageSize(limit);
		setCurrentPage(1);
	};

	const handlePaginationMetaChange = useCallback((meta: PaginationMeta) => {
		setPaginationMeta(meta);
	}, []);

	const displayedCount = useMemo(() => {
		if (!paginationMeta.total) {
			return 0;
		}

		const startIndex = (paginationMeta.page - 1) * paginationMeta.limit;
		const remaining = paginationMeta.total - startIndex;

		if (remaining <= 0) {
			return 0;
		}

		return Math.min(paginationMeta.limit, remaining);
	}, [paginationMeta.limit, paginationMeta.page, paginationMeta.total]);

	return (
		<div className="h-full flex flex-col overflow-hidden space-y-2 sm:space-y-3">
			<PageHeader
				title={t("billing.invoices.title")}
				description={t("billing.invoices.description")}
				actions={
					canManageBilling ? (
						<Link
							to="/app/org/$orgId/billing/new"
							params={{ orgId }}
							className="btn btn-primary btn-sm"
							data-testid="billing-add-button"
							aria-label={t("billing.invoices.actions.create")}
						>
							<Plus className="w-4 h-4" />
							<span className="hidden sm:inline">
								{t("billing.invoices.actions.create")}
							</span>
						</Link>
					) : undefined
				}
			/>

			<ListPageCard>
				<ListPageCardHeader className="space-y-3">
					{/* Search */}
					<div className="relative flex-1 sm:max-w-md">
						<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-base-content/40" />
						<Input
							type="search"
							placeholder={t("billing.invoices.searchPlaceholder")}
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							className="pl-10 pr-10"
							data-testid="billing-search-input"
							autoComplete="off"
							data-1p-ignore="true"
							data-lpignore="true"
							data-form-type="other"
						/>
						<div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
							{searchTerm && (
								<button
									type="button"
									onClick={() => setSearchTerm("")}
									className="text-base-content/40 hover:text-base-content/80 transition-colors"
									aria-label={t("common.clear")}
								>
									<X className="h-3 w-3" />
								</button>
							)}
							{searchTerm !== debouncedSearchTerm && (
								<div className="h-4 w-4 animate-spin rounded-full border-b-2 border-primary" />
							)}
						</div>
					</div>
					{/* Results count */}
					<div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-xs text-base-content/60">
						<span className="whitespace-nowrap">
							{t("common.showingResults", {
								count: displayedCount,
								total: paginationMeta.total,
							})}
						</span>
						{debouncedSearchTerm && (
							<>
								<span className="text-base-content/30">·</span>
								<span
									className="badge badge-ghost badge-sm max-w-50 truncate"
									title={debouncedSearchTerm}
								>
									{t("common.search")}:
									<span className="ml-1 font-medium">
										{debouncedSearchTerm}
									</span>
								</span>
							</>
						)}
					</div>
				</ListPageCardHeader>
				<ListPageCardContent>
					<div className="flex-1 overflow-hidden">
						<InvoiceList
							organizationId={orgId}
							pagination={{ page: currentPage, limit: pageSize }}
							searchTerm={debouncedSearchTerm}
							userId={userId}
							token={token}
							canManageBilling={canManageBilling}
							onPaginationMetaChange={handlePaginationMetaChange}
							onViewInvoice={handleViewInvoice}
						/>
					</div>
					<div className="shrink-0">
						<Pagination
							pagination={paginationMeta}
							onPageChange={handlePageChange}
							onPageSizeChange={handlePageSizeChange}
						/>
					</div>
				</ListPageCardContent>
			</ListPageCard>
		</div>
	);
}
